'use strict';

import { ValueCannotBeParsedError } from './ValueCannotBeParsedError';
import { InvalidConfigurationError } from './InvalidConfigurationError';
import { InvalidGlazingTypeError } from './InvalidGlazingTypeError';
import { Pricelist } from './Pricelist';
import { Configuration } from './Configuration';
import { Manifestation } from './Manifestation';
import { Door } from './Door';

class Partition {

    constructor() {

        /**
         * @type {Manifestation}
         * @private
         */
        this._manifestation = new Manifestation(this);
        this._manifestation.type = this._manifestation.dotsOrSquares;

        /**
         * @type {number}
         * @private
         */
        this._width = 0;

        /**
         * @type {number}
         * @private
         */
        this._widthWithWastage = 0;

        /**
         * @type {number}
         * @private
         */
        this._height = 0;

        /**
         * @type {number}
         * @private
         */
        this._heightWithWastage = 0;

        /**
         * @type {number}
         * @private
         */
        this._depth = 0;

        /**
         * @type {number}
         * @private
         */
        this._depthWithWastage = 0;

        /**
         * @type {string}
         * @private
         */
        this._type = '';

        /**
         * @type {number}
         * @private
         */
        this._glassThickness = 0;

        /**
         * @type {string}
         * @private
         */
        this._glazingType = '';

        /**
         * @type {string}
         * @private
         */
        this._acousticPerformance = this.singleGlazed33dB;

        /**
         * @type {Door}
         * @private
         */
        this._door = new Door(this);
        this._door.type = this._door.noDoor;

        /**
         * @type {object}
         */
        this._configuration = Configuration.get();

        /**
         * @type {object}
         */
        this._pricelist = Pricelist.get();
    }

    /**
     * @param {string|number} width
     */
    set width(width) {
        this._width = this._convertToFloatNumber(width);
        this._widthWithWastage = this._addWastage(this._width);
    }

    /**
     * @param {string|number} height
     */
    set height(height) {
        this._height = this._convertToFloatNumber(height);
        this._heightWithWastage = this._addWastage(this._height);
    }

    /**
     * @param {string|number} depth
     */
    set depth(depth) {
        this._depth = this._convertToFloatNumber(depth);
        this._depthWithWastage = this._addWastage(this._depth);
    }

    /**
     * @param {string} type
     */
    set type(type) {
        this._type = type;
    }

    /**
     * @param {string} glazingType
     */
    setGlazingType(glazingType) {
        if (this.hasValidDimensions === false) {
            throw new InvalidConfigurationError('Invalid Dimensions');
        }

        this._glazingType = glazingType;

        switch (glazingType) {
            case this.singleGlazingType:

                // Need to check acoustic performance in here
                if (this._measurementInMM(this._height) <= 2700) {

                    if (this.acousticPerformance === this.singleGlazed33dB) {
                        this._glassThickness = 1; // single glazed toughened 10mm
                    } else {
                        this._glassThickness = 4; // single glazed toughened 12mm
                    }

                } else {

                    if (this.acousticPerformance === this.singleGlazed33dB) {
                        this._glassThickness = 2; // single glazed, acoustic laminate 10.8mm
                    } else {
                        this._glassThickness = 5; // single glazed, acoustic laminate 12.8mm
                    }

                }

                break;
            case this.doubleGlazingType:
                this._glassThickness = 3; // double glazed, toughened 10mm & toughened 12mm (one sheet of each)
                break;
            default:
                throw new InvalidGlazingTypeError('Unknown Glazing Type "' + glazingType + '"');
        }
    }

    /**
     * @param {string} acousticPerformance
     */
    setAcousticPerformance(acousticPerformance) {
        this._acousticPerformance = acousticPerformance;
        // re set the glazing type to correctly set glass thickness with new acoustic performance
        this.setGlazingType(this._glazingType);
    }

    /**
     * @returns {string}
     */
    get acousticPerformance() {
        return this._acousticPerformance;
    }

    /**
     * @returns {Manifestation}
     */
    get manifestation() {
        return this._manifestation;
    }

    /**
     * @returns {Door}
     */
    get door() {
        return this._door;
    }

    /**
     * @returns {string}
     */
    get type() {
        return this._type;
    }

    /**
     * @returns {number}
     */
    get width() {
        return this._width;
    }

    /**
     * @returns {string}
     */
    get grandTotal() {
        let totalInPence = Math.ceil(this.subTotalInPence + this.marginTotalInPence);
        let totalInPounds = totalInPence / 100;
        // console.log('totalInPounds: ' + totalInPounds);
        return totalInPounds.toFixed(2);
    }

    /**
     * @returns {number}
     */
    get marginTotalInPence() {
        let margin = this.marginPercentage / 100;
        return Math.ceil(this.subTotalInPence * margin);
    }

    /**
     * @returns {number}
     */
    get marginPercentage() {
        if (this.isDoubleGlazed) {
            switch (this.type) {
                case this.inlineType:
                    return this._pricelist.margin.doubleGlazedInline;
                case this.cornerType:
                    return this._pricelist.margin.doubleGlazedCorner;
                case this.threeSidedType:
                    return this._pricelist.margin.doubleGlazedThreeSided;
                case this.tShapeType:
                    return this._pricelist.margin.doubleGlazedTShape;
            }
        } else {
            switch (this.type) {
                case this.inlineType:
                    return this._pricelist.margin.singleGlazedInline;
                case this.cornerType:
                    return this._pricelist.margin.singleGlazedCorner;
                case this.threeSidedType:
                    return this._pricelist.margin.singleGlazedThreeSided;
                case this.tShapeType:
                    return this._pricelist.margin.singleGlazedTShape;
            }
        }
    }

    /**
     * @returns {number}
     */
    get subTotalInPence() {
        return (
            this.tracksTotalInPence +
            this.gasketTotalInPence +
            this.abutmentTotalInPence +
            this.glassTotalInPence +
            this._door.totalInPence +
            this._manifestation.totalInPence +
            this.labourTotalInPence +
            this.additionalCostsInPence
        );
    }

    /**
     * @returns {number}
     */
    get height() {
        return this._height;
    }

    /**
     * @returns {number}
     */
    get depth() {
        return this._depth;
    }

    /**
     * @returns {string}
     */
    get singleGlazingType() {
        return 'single';
    }

    /**
     * @returns {string}
     */
    get doubleGlazingType() {
        return 'double';
    }

    /**
     * @returns {string}
     */
    get singleGlazed33dB() {
        return '33dB';
    }

    /**
     * @returns {string}
     */
    get singleGlazed38dB() {
        return '38dB';
    }

    /**
     * @returns {string}
     */
    get inlineType() {
        return 'Inline';
    }

    /**
     * @returns {string}
     */
    get cornerType() {
        return 'Corner';
    }

    /**
     * @returns {string}
     */
    get threeSidedType() {
        return 'Three Sided';
    }

    /**
     * @returns {string}
     */
    get tShapeType() {
        return 'T Shaped';
    }

    /**
     * @returns {number}
     */
    get glassThickness() {
        return this._glassThickness;
    }

    /**
     * @returns {number}
     */
    get numberOfTracks() {
        if (this.hasValidDimensions === false) {
            return 0;
        }

        let calculation = 0;
        switch (this._type) {
            case this.inlineType:
                calculation = (this._widthWithWastage / 3.6);
                break;
            case this.cornerType:
            case this.tShapeType:
                calculation = (this._widthWithWastage / 3.6) + (this._depthWithWastage / 3.6);
                break;
            case this.threeSidedType:
                calculation = (this._widthWithWastage / 3.6) + ((this._depthWithWastage / 3.6) * 2);
                break;
        }

        if (calculation > 0) {
            return Math.ceil(calculation / 1000);
        }

        return 0;
    }

    /**
     * @returns {number}
     */
    get gasketsInMeters() {
        if (this.hasValidDimensions === false) {
            return 0;
        }

        let calculation = 0;
        switch (this._type) {
            case this.inlineType:
                calculation = this._widthWithWastage;
                break;
            case this.cornerType:
            case this.tShapeType:
                calculation = (this._widthWithWastage + this._depthWithWastage);
                break;
            case this.threeSidedType:
                calculation = (this._widthWithWastage + (this._depthWithWastage * 2));
                break;
        }

        return Math.ceil(calculation / 1000);
    }

    /**
     * @returns {Array}
     */
    get gasketComponents() {
        let components = new Array();

        switch (this.glassThickness) {
            case 1:
            case 4:
                components = ['singleGlazedGaskets10mmGlass'];
                break;
            case 2:
                components = ['singleGlazedGaskets12mmGlass'];
                break;
            case 3:
            case 5:
                components = [
                    'doubleGlazedInnerGasket',
                    'singleGlazedGaskets10mmGlass',
                    'singleGlazedGaskets12mmGlass',
                ];
                break;
        }
        return components;
    }

    /**
     * @returns {number}
     */
    get numberOfAbutments() {
        if (this.hasValidDimensions === false) {
            return 0;
        }

        switch (this._type) {
            case this.tShapeType:
                return 3;
            default:
                return 2;
        }
    }

    /**
     * @returns {number}
     */
    get glassSurfaceArea() {
        if (this.hasValidDimensions === false) {
            return 0;
        }

        let calculation = 0;
        switch (this._type) {
            case this.inlineType:
                calculation = (this._measurementInMM(this._widthWithWastage) * this._measurementInMM(this._heightWithWastage));
                break;
            case this.cornerType:
            case this.tShapeType:
                calculation = (this._measurementInMM(this._widthWithWastage) + this._measurementInMM(this._depthWithWastage)) * this._measurementInMM(this._heightWithWastage);
                break;
            case this.threeSidedType:
                calculation = this._measurementInMM(this._widthWithWastage) + (this._measurementInMM(this._depthWithWastage) * 2);
                calculation *= this._measurementInMM(this._heightWithWastage);
                break;
        }

        if (calculation > 0) {

            // Calculation is now mm square so we need to move the decimal point 6 places and round up to nearest metre
            return Math.ceil(calculation / 1000000);
        }

        return calculation;
    }

    /**
     * @returns {boolean}
     */
    get hasValidDimensions() {
        switch (this._type) {
            case this.inlineType:
                return (this._width !== 0 && this._height !== 0);
            case this.cornerType:
            case this.threeSidedType:
            case this.tShapeType:
                return (this._width !== 0 && this._height !== 0 && this._depth !== 0);
            default:
                return true;
        }

        return false;
    }

    /**
     * @returns {number}
     */
    get additionalCostsInPence() {
        return this._pricelist.additional.general + this._pricelist.additional.delivery;
    }

    /**
     * @returns {number}
     */
    get tracksTotalInPence() {
        let numberOfTracks = this.numberOfTracks;
        let trackComponentsTotalInPence = 0;
        let priceList = this._pricelist;
        let components = this._configuration.singleGlazed.trackComponents;

        if (this.isDoubleGlazed === true) {
            components = this._configuration.doubleGlazed.trackComponents;
        }

        components.forEach(function (componentKey) {
            trackComponentsTotalInPence += (priceList.fixings[componentKey] * numberOfTracks);
        });

        return trackComponentsTotalInPence;
    }

    /**
     * @returns {number}
     */
    get gasketTotalInPence() {
        let gasketInMeters = this.gasketsInMeters;
        let gasketComponentsTotalInPence = 0;
        let components = this.gasketComponents;
        let priceList = this._pricelist;

        components.forEach(function (componentKey) {
            gasketComponentsTotalInPence += priceList.fixings[componentKey] * gasketInMeters;
        });

        return gasketComponentsTotalInPence;
    }

    /**
     * @returns {number}
     */
    get abutmentTotalInPence() {
        let numberOfAbutments = this.numberOfAbutments;
        let abutmentsComponentsTotalInPence = 0;
        let priceList = this._pricelist;
        let components = this._configuration.singleGlazed.abutmentComponents;

        if (this.isDoubleGlazed === true) {
            components = this._configuration.doubleGlazed.abutmentComponents;
        }

        components.forEach(function (componentKey) {
            abutmentsComponentsTotalInPence += priceList.fixings[componentKey] * numberOfAbutments;
        });

        return abutmentsComponentsTotalInPence;
    }

    /**
     * @returns {number}
     */
    get glassTotalInPence() {
        let glassSurfaceArea = this.glassSurfaceArea;
        // console.log('glassSurfaceArea: ' + glassSurfaceArea);
        let priceList = this._pricelist;
        // console.log('priceList.glass[this.glassThickness]: ' + priceList.glass[this.glassThickness]);
        let glassTotalInPence = glassSurfaceArea * priceList.glass[this.glassThickness];

        return Math.ceil(glassTotalInPence);
    }

    /**
     * @returns {number}
     */
    get labourTotalInPence() {
        return this._pricelist.labour.total;
    }

    /**
     * @returns {boolean}
     */
    get isDoubleGlazed() {
        return Boolean(this.glassThickness === 3);
    }

    /**
     * @param {number|string} value
     * @returns {*}
     * @private
     */
    _convertToFloatNumber(value) {
        if (value === '') {
            value = 0;
        }
        if (typeof value !== 'number') {
            value = Number.parseFloat(value);
        }
        this._isThisANumber(value);
        return value;
    }

    /**
     * @param {*} value
     * @private
     */
    _isThisANumber(value) {
        if (Number.isNaN(value)) {
            throw new ValueCannotBeParsedError();
        }
    }

    /**
     * @param {number} value
     * @return {number}
     * @private
     */
    _addWastage(value) {
        return (0.08 * value) + value;
    }

    /**
     * @param {number} measurement
     * @private
     */
    _measurementInMM(measurement) {
        return measurement;
    }
}

export { Partition };



// WEBPACK FOOTER //
// ./src/objects/Partition.js